// src/components/BuildingDesigner/geoUtils.js
//
// Pure geometry helpers used by the canvas and the forms.
// No React, no side effects — easy to test.

// Snap a world coordinate to the current sub-grid step (in metres).
// Step depends on zoom level so the user can place at 1cm precision.
export function snap(v, step) {
  return Math.round(v / step) * step;
}

// Which wall of a rectangular room does world (px, py) touch?
// Returns "south" | "north" | "west" | "east" | null.
export function detectRectWall(room, px, py, tol = 0.05) {
  const { x, y, width, height } = room;
  const x1 = x + width, y1 = y + height;
  if (px < x - tol || px > x1 + tol) return null;
  if (py < y - tol || py > y1 + tol) return null;
  if (Math.abs(py - y)  < tol) return "south";
  if (Math.abs(py - y1) < tol) return "north";
  if (Math.abs(px - x)  < tol) return "west";
  if (Math.abs(px - x1) < tol) return "east";
  return null;
}

// Distance along a rect wall from its start point (SW=south/west corner,
// NE=east/north corner). Matches your Python world_to_wall_dist logic.
export function distAlongRectWall(room, side, px, py) {
  const { x, y, width, height } = room;
  if (side === "south" || side === "north") return px - x;
  if (side === "west"  || side === "east")  return py - y;
  return 0;
}

// For a given rect room + wall + position, compute the world endpoints
// of an opening [pos, pos+width] on that wall — used for drawing markers.
export function rectOpeningEndpoints(room, side, pos, width) {
  const { x, y, width: w, height: h } = room;
  const x1 = x + w, y1 = y + h;
  if (side === "south") return [[x + pos, y],  [x + pos + width, y]];
  if (side === "north") return [[x + pos, y1], [x + pos + width, y1]];
  if (side === "west")  return [[x,  y + pos], [x,  y + pos + width]];
  if (side === "east")  return [[x1, y + pos], [x1, y + pos + width]];
  return [[0, 0], [0, 0]];
}

// For a DOOR the JSON `position` is the HINGE point (per Python schema).
// The gap extends in the direction determined by hinge_side:
//   hinge_side = "end"   → gap is [position − width, position]
//   hinge_side = "start" → gap is [position, position + width]
// This helper returns the two world-space endpoints of the gap.
export function rectDoorEndpoints(room, side, pos, width, hingeSide) {
  const startAlong = hingeSide === "end" ? (pos - width) : pos;
  return rectOpeningEndpoints(room, side, startAlong, width);
}

// Same, for open-door: `position` is the near edge (matches Python).
export function rectOpenDoorEndpoints(room, side, pos, width) {
  return rectOpeningEndpoints(room, side, pos, width);
}

// Windows are CENTRE-based: `position` is the centre, gap is
// [position − width/2, position + width/2].
export function rectWindowEndpoints(room, side, pos, width) {
  return rectOpeningEndpoints(room, side, pos - width / 2, width);
}

// ─────────────────────────────────────────────────────────────────────
// CUSTOM polygon helpers
// ─────────────────────────────────────────────────────────────────────

// Compute the centroid (average of vertices) — used for placing the label.
export function polygonCentroid(points) {
  if (!points || points.length === 0) return { x: 0, y: 0 };
  let sx = 0, sy = 0;
  for (const [x, y] of points) { sx += x; sy += y; }
  return { x: sx / points.length, y: sy / points.length };
}

// Distance from a point to a segment [(ax,ay)-(bx,by)]. Returns
// { d: perpDistance, t: paramAlong[0..1], px, py: nearestPointOnSeg }.
export function distToSegment(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay;
  const L2 = dx * dx + dy * dy;
  if (L2 < 1e-12) {
    const d = Math.hypot(px - ax, py - ay);
    return { d, t: 0, px: ax, py: ay };
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / L2;
  if (t < 0) t = 0;
  if (t > 1) t = 1;
  const qx = ax + t * dx, qy = ay + t * dy;
  return { d: Math.hypot(px - qx, py - qy), t, px: qx, py: qy };
}

// Detect which edge of a custom polygon the mouse is near.
// Returns { edgeKey: "edgeN", index, t: [0..1], along: metres,
//           edgeLen, edgeStart:[x,y], edgeEnd:[x,y] } or null.
// `closed` decides whether the last→first edge is considered.
export function detectCustomEdge(points, closed, mx, my, tol = 0.35) {
  if (!points || points.length < 2) return null;
  const n = points.length;
  const nEdges = closed ? n : n - 1;
  let best = null;
  for (let i = 0; i < nEdges; i++) {
    const [ax, ay] = points[i];
    const [bx, by] = points[(i + 1) % n];
    const r = distToSegment(mx, my, ax, ay, bx, by);
    if (r.d <= tol && (best === null || r.d < best.d)) {
      const edgeLen = Math.hypot(bx - ax, by - ay);
      best = {
        edgeKey: `edge${i + 1}`,
        index: i,
        t: r.t,
        along: r.t * edgeLen,
        edgeLen,
        edgeStart: [ax, ay],
        edgeEnd: [bx, by],
      };
    }
  }
  return best;
}

// ─── Polygon orientation — mirrors Python _polygon_is_clockwise ───
// Shoelace-sign in Y-up: > 0 → clockwise. For OPEN polylines the caller
// is expected to force poly_cw = true (matching Python line 690 of main.py:
// "poly_cw = True if not closed else _polygon_is_clockwise(points)").
export function polygonIsClockwise(pts) {
  if (!pts || pts.length < 3) return true;
  let s = 0;
  const n = pts.length;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = pts[i];
    const [x2, y2] = pts[(i + 1) % n];
    s += (x2 - x1) * (y2 + y1);
  }
  return s > 0;
}

// Mirrors Python _edge_swing (main.py line 675).
// Given the polygon's clockwise-ness and the user's CW/CCW rotation choice,
// return {swing, hingeSide} the way the CLI stores it, so DXF renders
// identically.
export function edgeSwing(polyCw, rotation) {
  const rotCw = rotation === "clockwise";
  const swing = rotCw ? "right" : "left";
  let hingeSide;
  if (polyCw) {
    hingeSide = rotCw ? "end" : "start";
  } else {
    hingeSide = rotCw ? "start" : "end";
  }
  return { swing, hingeSide };
}

// Endpoints of an opening [along, along+width] on a custom edge (world).
// `edgeInfo` is what detectCustomEdge returned, or an equivalent
// {edgeStart:[ax,ay], edgeEnd:[bx,by], edgeLen}. `along` is metres from
// edge start (0..edgeLen).
export function customEdgeOpeningEndpoints(edgeInfo, along, width) {
  const [ax, ay] = edgeInfo.edgeStart;
  const [bx, by] = edgeInfo.edgeEnd;
  const L = edgeInfo.edgeLen || Math.hypot(bx - ax, by - ay) || 1;
  const ux = (bx - ax) / L, uy = (by - ay) / L;
  return [
    [ax + ux * along,           ay + uy * along],
    [ax + ux * (along + width), ay + uy * (along + width)],
  ];
}

// For a DOOR on a custom edge: `position` is the HINGE point along the
// edge; gap extends per hingeSide (matching Python _custom_edge_openings).
export function customDoorEndpoints(edgeInfo, position, width, hingeSide) {
  const startAlong = hingeSide === "end" ? (position - width) : position;
  return customEdgeOpeningEndpoints(edgeInfo, startAlong, width);
}

// For a WINDOW on a custom edge: `position` is the CENTRE, gap is
// [position − width/2, position + width/2].
export function customWindowEndpoints(edgeInfo, position, width) {
  return customEdgeOpeningEndpoints(edgeInfo, position - width / 2, width);
}

// For an OPEN DOOR on a custom edge: `position` is the NEAR EDGE
// (matches Python's convention for open doors).
export function customOpenDoorEndpoints(edgeInfo, position, width) {
  return customEdgeOpeningEndpoints(edgeInfo, position, width);
}

// Build an edgeInfo dict from a raw polygon + edge index (used when we
// don't have detectCustomEdge in hand — e.g. rendering already-committed
// openings that store wall="edgeN" and position).
export function edgeInfoFromIndex(points, edgeIndex, closed) {
  const n = points.length;
  const i = edgeIndex;
  const [ax, ay] = points[i];
  const [bx, by] = points[(i + 1) % n];
  const edgeLen = Math.hypot(bx - ax, by - ay);
  return {
    edgeKey: `edge${i + 1}`, index: i, t: 0, along: 0,
    edgeLen,
    edgeStart: [ax, ay], edgeEnd: [bx, by],
  };
}

// Parse "edgeN" → zero-based edge index (or -1 if malformed).
export function edgeKeyToIndex(edgeKey) {
  if (!edgeKey || typeof edgeKey !== "string") return -1;
  const m = /^edge(\d+)$/.exec(edgeKey);
  return m ? (parseInt(m[1], 10) - 1) : -1;
}

// Snap a click while placing polygon points.
// Priority (per user spec):
//   1. Snap to any existing point within 0.4 m (used to close the loop)
//   2. Snap to axis-aligned with the previous point within 0.2 m
//   3. Fall through to the 1 m grid snap
// Returns { x, y, kind } where kind ∈ "existing" | "axis" | "grid",
// and optionally { axisPoint: [x,y] } showing the anchor for the axis
// hint line so the canvas can render a guide.
export function snapCustomPoint(mx, my, existingPoints) {
  const SNAP_EXISTING = 0.4;
  const SNAP_AXIS     = 0.2;
  const GRID          = 1.0;

  // 1) existing point
  if (existingPoints && existingPoints.length) {
    let best = null;
    for (let i = 0; i < existingPoints.length; i++) {
      const [px, py] = existingPoints[i];
      const d = Math.hypot(mx - px, my - py);
      if (d <= SNAP_EXISTING && (best === null || d < best.d)) {
        best = { d, x: px, y: py, index: i };
      }
    }
    if (best) return { x: best.x, y: best.y, kind: "existing",
                       existingIndex: best.index };
  }

  // 2) axis-aligned with the previous point
  if (existingPoints && existingPoints.length > 0) {
    const [lx, ly] = existingPoints[existingPoints.length - 1];
    // horizontal — same y as previous
    const dy = Math.abs(my - ly);
    // vertical — same x as previous
    const dx = Math.abs(mx - lx);
    if (dy <= SNAP_AXIS && dy <= dx) {
      const gx = Math.round(mx / GRID) * GRID;
      return { x: gx, y: ly, kind: "axis", axisPoint: [lx, ly],
               axisDir: "horizontal" };
    }
    if (dx <= SNAP_AXIS && dx < dy) {
      const gy = Math.round(my / GRID) * GRID;
      return { x: lx, y: gy, kind: "axis", axisPoint: [lx, ly],
               axisDir: "vertical" };
    }
  }

  // 3) grid snap
  return {
    x: Math.round(mx / GRID) * GRID,
    y: Math.round(my / GRID) * GRID,
    kind: "grid",
  };
}

// Choose the sub-grid step based on how many pixels one metre takes.
// > 200 px/m → show 1 cm; > 60 → 10 cm; else → 1 m.
export function chooseGridStep(ppm) {
  if (ppm > 200) return 0.01;
  if (ppm > 60)  return 0.1;
  return 1.0;
}
